// +----------------------------------------------------------------------
// | 决策复盘 /decision/:id/result：事后反馈
// | 反馈是产品长期粘性的核心 —— 1 天后 / 7 天后 / 30 天后都可以回来补
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SatisfactionPicker, { satisfactionLabel } from '../../components/SatisfactionPicker';
import { toast } from '../../components/Toast';
import { getDecision, getResult, submitResult } from '../../api/decision';
import { categoryLabel } from '../../lib/categoryMap';
import { formatFull } from '../../lib/format';
import './DecisionDetail.less';

export default function DecisionDetail() {
  const { id } = useParams();

  const [decision, setDecision] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [satisfaction, setSatisfaction] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const detail = await getDecision(id);
      setDecision(detail);

      try {
        const existing = await getResult(id);
        if (existing && (existing.satisfaction || existing.feedback)) {
          setResult(existing);
          setSatisfaction(Number(existing.satisfaction) || 0);
          setFeedback(existing.feedback || '');
        }
      } catch (e) {
        // 还没反馈过，属正常情况
      }
    } catch (e) {
      setLoadError(e.message || '打不开这个决定');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!satisfaction) {
      toast('先选一个感受吧');
      return;
    }

    setSubmitting(true);
    try {
      const data = await submitResult(id, {
        satisfaction: Number(satisfaction),
        feedback: feedback.trim(),
      });
      setResult(data || { satisfaction, feedback });
      toast('记下了，下次我会参考这个', 'success');
    } catch (err) {
      toast(err.message || '没提交上，再试一次', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="page center muted">正在打开…</div>;
  }

  if (loadError) {
    return (
      <div className="page">
        <div className="card center detail-error">
          <p>{loadError}</p>
          <Link to="/history" className="btn btn-ghost btn-sm detail-error-btn">
            回到我的决定
          </Link>
        </div>
      </div>
    );
  }

  if (!decision) return null;

  const selected = decision.selected_option;

  return (
    <div className="page detail">
      <Link to={`/decision/${id}`} className="detail-back muted">
        ‹ 看看这次是怎么想的
      </Link>

      <header className="detail-head">
        <h1 className="detail-question">{decision.title || decision.question}</h1>
        <div className="detail-tags">
          {decision.category && <span className="tag">{categoryLabel(decision.category)}</span>}
        </div>
      </header>

      <div className="card detail-chosen">
        <span className="detail-chosen-label">当时的决定</span>
        <strong className="detail-chosen-name">{selected ? selected.name : '还没记录选择'}</strong>
        {decision.decided_at && (
          <span className="muted">{formatFull(decision.decided_at)}</span>
        )}
      </div>

      {result && result.satisfaction ? (
        <section className="card detail-result">
          <div className="detail-section-title">你当时的反馈</div>
          <p className="detail-result-satisfaction">{satisfactionLabel(result.satisfaction)}</p>
          {result.feedback && <p className="detail-result-feedback">{result.feedback}</p>}
          {result.result_summary && (
            <p className="muted detail-result-summary">{result.result_summary}</p>
          )}
          {result.ai_learning && (
            <div className="detail-learning">
              <span className="detail-learning-title">这次学到的</span>
              <p>{result.ai_learning}</p>
              <span className="muted">
                会记进「AI 记住的我」，下次遇到同类问题自动参考。
              </span>
            </div>
          )}
          <p className="muted detail-result-note">
            想改了随时重新说一遍，以最新的为准。
          </p>
        </section>
      ) : (
        <div className="card detail-hint">
          <p>这个决定后来怎么样？</p>
          <p className="muted">
            不用马上回答。过上几天，等你真的有感受了再回来补一句就行 —— 这会让我下次更懂你。
          </p>
        </div>
      )}

      <form className="card" onSubmit={handleSubmit}>
        <div className="detail-section-title">现在回头看，感觉如何</div>
        <SatisfactionPicker
          value={satisfaction}
          onChange={setSatisfaction}
          disabled={submitting}
        />

        <div className="field detail-feedback-field">
          <label htmlFor="feedback">想多说两句？（可不填）</label>
          <textarea
            id="feedback"
            className="input"
            rows={3}
            maxLength={300}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="比如：去了以后发现其实挺累。"
          />
        </div>

        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          {submitting ? '提交中…' : result && result.satisfaction ? '更新反馈' : '提交反馈'}
        </button>
      </form>
    </div>
  );
}
